import React, { useContext } from 'react';
import styled from 'styled-components';
import { Trans } from '@lingui/react';
import { Box, Flex } from '@components/layout/Grid';
import Button from '@components/buttons';
import LanguageContext from '@lib/contexts/languageContext';
import { CameraIcon } from '@assets/index';
import { ProgramCategory, ProgramLiveEvent } from '@gql/generated';
import { isPast } from 'date-fns/isPast';
import { addDays } from 'date-fns/addDays';

const Wrapper = styled.div<{ category: ProgramCategory }>`
  min-height: 60vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 50px 20px;
  background-color: ${({ category }) =>
    category === ProgramCategory['Challenges'] ? '#D9E8E7' : '#e3f4fe'};

  svg {
    height: 64px;
    width: 64px;

    path {
      fill: ${({ category, theme }) => {
        return theme.helpers.getProgramThemeColor(category);
      }};
    }
  }
`;

const Title = styled.p`
  font-size: 20px;
  font-weight: 300;
  line-height: 1.4;
  text-align: center;
  margin: 30px 0 20px 0;
`;

const DescriptionText = styled.p`
  max-width: 650px;
  font-size: 14px;
  text-align: center;
  margin-top: 0;
  margin-bottom: 20px;
`;

const OpenSurvey = styled.button`
  height: 44px;
  color: #1c343e;
  font-size: 13px;
  font-weight: 600;
  line-height: 16px;
  text-decoration: none;
  border: 1px solid;
  border-radius: 3px;
  padding: 11px 24px;
  margin-left: 10px;
  background-color: transparent;
  cursor: pointer;
`;

const JoinProgram = styled.p`
  font-size: 16px;
  color: #1c343e;
  margin-bottom: 20px;

  i {
    margin-right: 6px;

    :before {
      color: ${({ theme }) => theme.colors.secondary['gray-2']};
    }
  }
`;

declare type Props = {
  programId: string;
  element: ProgramLiveEvent;
  hasAccess: boolean;
  programCategory: ProgramCategory;
};

const LiveEventElementWindow = (props: Props) => {
  const lang = useContext(LanguageContext);
  const { programId, element, hasAccess, programCategory } = props;

  const dates = element?.liveEvent?.eventDates;

  if (!dates) {
    return null;
  }

  const lastDate = dates[dates.length - 1];
  let recordingsExpired = false;
  if (isPast(addDays(new Date(lastDate), 3))) {
    recordingsExpired = true;
  }

  let content = null;

  if (programCategory === ProgramCategory['Courses'] && recordingsExpired) {
    content = (
      <>
        <DescriptionText data-testid="LiveEventElementOverlay--Description">
          <Trans id="weeklyProgram.live_event.event_recording_expired" />
        </DescriptionText>
      </>
    );
  } else if (!hasAccess) {
    content = (
      <>
        <JoinProgram>
          <Trans id="weeklyProgram.live_event.locked" />
        </JoinProgram>
        <Button
          appearance={
            programCategory === ProgramCategory['Challenges']
              ? 'green'
              : 'stone'
          }
          href={`/${lang}/${programCategory}/${programId}/get-started`}
        >
          <Trans id="metadata.getStarted" />
        </Button>
      </>
    );
  } else if (element?.liveEvent?.isExpired || element?.liveEvent?.isClosed) {
    content = (
      <>
        <DescriptionText data-testid="LiveEventElementOverlay--Description">
          <Trans id="weeklyProgram.live_event.event_expired" />
        </DescriptionText>
      </>
    );
  } else {
    content = (
      <>
        <DescriptionText data-testid="LiveEventElementOverlay--Description">
          {element.description}
        </DescriptionText>
        <Flex mt={20}>
          <Button
            appearance={
              programCategory === ProgramCategory['Challenges']
                ? 'green'
                : 'stone'
            }
            href={
              hasAccess
                ? `/${lang}/live/${element?.liveEvent?.slug}`
                : `/${lang}/${programCategory}/${programId}/get-started`
            }
          >
            {hasAccess ? (
              <Trans id="weeklyProgram.live_event.visit_event" />
            ) : (
              <Trans id="metadata.getStarted" />
            )}
          </Button>
        </Flex>
      </>
    );
  }

  return (
    <Box data-testid="VideoPlayerSection">
      <Wrapper
        category={programCategory}
        data-testtype="LiveEventElementOverlay"
      >
        <CameraIcon />
        <Title data-testid="LiveEventElementOverlay--Title">
          {element.title ?? element.liveEvent?.title}
        </Title>
        {content}
      </Wrapper>
    </Box>
  );
};

export default LiveEventElementWindow;
